/**
 * Timeline readers for the note-push candidate-mapping store.
 *
 * These parse the note-push tool-result payloads off the agent-harness timeline
 * into the display shapes the ingest atoms consume. They are harness-coupled
 * (``ToolUseItem`` / ``readToolResultJsonCandidates``), so they live with the
 * Work harness while the pure state core lives in the note-push domain; keeping
 * them apart avoids a note-push → projects dependency.
 *
 * Two sources feed the store, both off the timeline:
 *   (a) ``get_note_push_hints`` results — the structured
 *       ``result_provider_metadata.data`` when present, with a reload fallback
 *       that parses the JSON out of the tool result text
 *       ({@link readNotePushHintsData}).
 *   (b) ``FIELD:`` lines from a ``run_computer_use_task`` read-only discovery
 *       run, parsed via {@link readComputerUseFieldCandidates}.
 */

import type { ToolUseItem } from '../../../contracts/harness.types';
import { isRecord } from '../../../contracts/parse-utils';
import type {
  NotePushHintsData,
  NotePushRecordedData,
} from '../../../contracts/projects/note-push-mapping.types';
import {
  parseNotePushReport,
  type NotePushReportField,
} from '../../../contracts/projects/note-push-report';
import {
  readToolResultJsonCandidates,
  readToolResultText,
} from '../../../contracts/toolResult';

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  isRecord(value) ? value : undefined;

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];

/** The ``template_id`` + ``app_name`` every note-push result must carry. */
const coerceIdentified = (
  value: unknown
):
  | { record: Record<string, unknown>; template_id: string; app_name: string }
  | undefined => {
  const record = asRecord(value);
  if (!record) return undefined;
  const templateId = record.template_id;
  const appName = record.app_name;
  if (typeof templateId !== 'string' || typeof appName !== 'string') return undefined;
  return { record, template_id: templateId, app_name: appName };
};

/** The ``[{ section_heading, ui_targets }]`` rows under ``record[key]``. */
const coerceHeadingRows = (
  record: Record<string, unknown>,
  key: string
): { section_heading: string; ui_targets: string[] }[] => {
  const rows = record[key];
  if (!Array.isArray(rows)) return [];
  return rows.flatMap((entry) => {
    const row = asRecord(entry);
    if (!row || typeof row.section_heading !== 'string') return [];
    return [
      { section_heading: row.section_heading, ui_targets: asStringArray(row.ui_targets) },
    ];
  });
};

const coerceHintsData = (value: unknown): NotePushHintsData | undefined => {
  const base = coerceIdentified(value);
  if (!base) return undefined;
  return {
    template_id: base.template_id,
    app_name: base.app_name,
    template_sections: asStringArray(base.record.template_sections),
    hints: coerceHeadingRows(base.record, 'hints'),
  };
};

const coerceRecordedData = (value: unknown): NotePushRecordedData | undefined => {
  const base = coerceIdentified(value);
  if (!base) return undefined;
  return {
    template_id: base.template_id,
    app_name: base.app_name,
    recorded: coerceHeadingRows(base.record, 'recorded'),
  };
};

/**
 * Read a note-push structured result off a timeline tool-use item, via the
 * shared candidate ladder (live ``result_provider_metadata.data``, else JSON
 * parsed out of the tool result text, else a ``{ data: … }`` envelope). Returns
 * the first candidate that coerces, or ``undefined``.
 */
const readVia = <T>(
  item: ToolUseItem,
  coerce: (value: unknown) => T | undefined
): T | undefined => {
  for (const candidate of readToolResultJsonCandidates(item)) {
    const data = coerce(candidate);
    if (data) return data;
  }
  return undefined;
};

export const readNotePushHintsData = (item: ToolUseItem): NotePushHintsData | undefined =>
  readVia(item, coerceHintsData);

export const readNotePushRecordedData = (
  item: ToolUseItem
): NotePushRecordedData | undefined => readVia(item, coerceRecordedData);

/**
 * Read FIELD candidates out of a ``run_computer_use_task`` result's free-text
 * report (a read-only discovery run).
 *
 * Gated on patient verification *and grammar order*: only ``FIELD:`` lines that
 * appear after a ``PATIENT: verified`` line qualify ({@link
 * NotePushReport.verifiedFields}). A missing / ``mismatch`` / ``unverified``
 * identity — or a ``FIELD:`` emitted before the verification line — means the
 * fields may have been read from the wrong chart, so they must never reach the
 * mapping panel. Mirrors the BE brief, which requires identity confirmation as
 * the first report line, before any FIELD is reported.
 */
export const readComputerUseFieldCandidates = (
  item: ToolUseItem
): NotePushReportField[] => parseNotePushReport(readToolResultText(item)).verifiedFields;
